import { useState, useMemo, useRef, useCallback } from 'react'
import classNames from 'classnames'
import Tippy from '@tippyjs/react'

import Button from './Button'
import { useWholesaleAIMutation } from '../services/wholesale'
import Flag from 'react-flagkit'

let extractAIText = (value) => {
  if (!value) {
    return ''
  }

  if (typeof value === 'string') {
    return value
  }

  if (Array.isArray(value)) {
    return value.map(extractAIText).filter(Boolean).join('\n\n')
  }

  if (typeof value === 'object') {
    for (let key of ['response', 'message', 'summary', 'text', 'data']) {
      if (value[key]) {
        let extracted = extractAIText(value[key])
        if (extracted) {
          return extracted
        }
      }
    }
    return JSON.stringify(value, null, 2)
  }

  return String(value)
}

export default function AICheckButton({ asin, region, cost }) {
  let { mutateAsync, isPending } = useWholesaleAIMutation()
  let [isOpen, setIsOpen] = useState(false)
  let [response, setResponse] = useState(null)
  let [error, setError] = useState(null)
  let cacheRef = useRef({})

  let appendTo = useMemo(() => {
    if (typeof document === 'undefined') {
      return undefined
    }
    return () => document.body
  }, [])

  let cacheKey = useMemo(() => {
    if (!asin) {
      return null
    }
    let keyParts = [asin]
    if (region) {
      keyParts.push(region)
    }
    if (cost !== undefined && cost !== null && cost !== '') {
      keyParts.push(String(cost))
    }
    return keyParts.join('::')
  }, [asin, region, cost])

  let formattedResponse = useMemo(() => extractAIText(response), [response])
  let hasCachedResponse = cacheKey ? Boolean(cacheRef.current[cacheKey]) : false

  let handleClick = useCallback(async () => {
    if (!asin || isPending) {
      return
    }

    setIsOpen(true)

    if (cacheKey && cacheRef.current[cacheKey]) {
      setResponse(cacheRef.current[cacheKey])
      setError(null)
      return
    }

    setError(null)
    setResponse(null)

    try {
      let data = await mutateAsync({ asin, region, cost })
      if (cacheKey) {
        cacheRef.current[cacheKey] = data
      }
      setResponse(data)
    } catch (err) {
      setError(err?.message || 'Fehler beim Abrufen der AI-Antwort')
    }
  }, [asin, cacheKey, cost, isPending, mutateAsync, region])

  let content = (
    <div className='text-sm leading-relaxed text-left max-w-[360px] max-h-[360px] overflow-y-auto whitespace-pre-wrap p-3 bg-white text-neutral-800 dark:bg-neutral-900 dark:text-neutral-100 rounded-md shadow-lg'>
      <header className='flex items-center justify-between gap-3 mb-2'>
        <span className='uppercase tracking-wider text-[10px] text-neutral-500 dark:text-neutral-400 flex items-center gap-1'>
          <span>AI Analyse</span>
          {region ? <Flag country={region.toUpperCase()} size={10} /> : null}
        </span>
        <button
          className='text-xs text-neutral-500 hover:text-neutral-800 dark:text-neutral-400 dark:hover:text-neutral-100'
          onClick={() => setIsOpen(false)}
        >
          Schließen
        </button>
      </header>
      <div>
        {isPending && cacheKey && !cacheRef.current[cacheKey] && !response && !error && (
          <div className='flex items-center gap-2 text-neutral-500 dark:text-neutral-300'>
            <i className='bx bx-loader-alt animate-spin text-lg'></i>
            <span>AI analysiert das Produkt …</span>
          </div>
        )}
        {!isPending && error && (
          <div className='text-red-500 dark:text-red-400'>{error}</div>
        )}
        {!isPending && !error && formattedResponse && (
          <div className='text-neutral-700 dark:text-neutral-100 whitespace-pre-wrap'>{formattedResponse}</div>
        )}
        {!isPending && !error && !formattedResponse && (
          <div className='text-neutral-500 dark:text-neutral-300'>Keine Daten verfügbar.</div>
        )}
      </div>
    </div>
  )

  return (
    <Tippy
      visible={isOpen}
      onClickOutside={() => setIsOpen(false)}
      interactive
      appendTo={appendTo}
      placement='bottom'
      maxWidth='none'
      content={content}
    >
      <span>
        <Button
          variant={(formattedResponse || hasCachedResponse) ? 'primary' : 'primary-light'}
          className='min-w-max flex items-center gap-2 text-sm font-semibold'
          onClick={handleClick}
          disabled={isPending || !asin}
          aria-busy={isPending}
        >
          <span className={classNames('text-base leading-none', {
            'text-black': formattedResponse,
            'text-white': !formattedResponse,
          })}>✨</span>
          <span className='flex items-center gap-2'>
            <span>Ai Check</span>
            {region ? <Flag country={region.toUpperCase()} size={16} /> : null}
          </span>
        </Button>
      </span>
    </Tippy>
  )
}
